import { Bell, Mail, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { useInadimplentes, useResendChargeReminder } from '../../hooks/use-dashboard';

const brl = (v: number): string =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * Story 6.2 — Top 10 clientes inadimplentes por valor vencido.
 * Botão [Lembrar] dispara `POST /api/dashboard/inadimplentes/:chargeId/resend-reminder`
 * usando `oldest_charge_id` da agregação backend.
 */
export function InadimplentesTable() {
  const query = useInadimplentes(10);
  const resend = useResendChargeReminder();

  if (query.isLoading) {
    return <Skeleton />;
  }

  if (query.isError) {
    return (
      <div className="rounded-lg border border-bad/40 bg-bad/10 p-4 text-sm text-ink">
        Erro ao carregar inadimplentes.
      </div>
    );
  }

  const items = query.data?.data.items ?? [];
  if (items.length === 0) {
    return (
      <Card title="Inadimplentes (Top 10)">
        <div className="py-8 text-center text-sm text-muted">
          Nenhum cliente inadimplente no momento ✅
        </div>
      </Card>
    );
  }

  const handleRemind = (chargeId: string, clientName: string) => {
    resend.mutate(chargeId, {
      onSuccess: (data) => {
        const channel = data.data.channel ?? '—';
        const template = data.data.template_key;
        toast.success(`Lembrete enviado pra ${clientName} via ${channel} (${template})`);
      },
      onError: () => {
        toast.error(`Falha ao enviar lembrete pra ${clientName}`);
      },
    });
  };

  return (
    <Card title="Inadimplentes (Top 10)">
      <table className="w-full text-sm">
        <thead className="border-b border-line bg-panel-2 text-left text-[11px] uppercase tracking-wider text-muted-3">
          <tr>
            <th className="px-3 py-2 font-medium">Cliente</th>
            <th className="px-3 py-2 text-right font-medium">Vencido</th>
            <th className="px-3 py-2 text-right font-medium">Cobranças</th>
            <th className="px-3 py-2 text-right font-medium">Dias atraso</th>
            <th className="px-3 py-2 font-medium">Canal</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr key={row.client_id} className="border-b border-line last:border-0 hover:bg-panel-2">
              <td className="px-3 py-2">
                <Link
                  to={`/clients/${row.client_id}`}
                  className="font-medium text-ink hover:underline"
                >
                  {row.client_name}
                </Link>
                <div className="text-xs text-muted-2">
                  {row.client_type === 'pessoa_fisica' ? 'PF' : 'PJ'}
                </div>
              </td>
              <td className="px-3 py-2 text-right font-mono tabular-nums text-ink">
                {brl(row.total_overdue)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-ink-2">{row.charges_count}</td>
              <td className="px-3 py-2 text-right tabular-nums text-bad">{row.days_overdue}d</td>
              <td className="px-3 py-2">
                <div className="flex gap-1">
                  {row.whatsapp && (
                    <MessageCircle className="h-4 w-4 text-good" aria-label="WhatsApp ok" />
                  )}
                  {row.email_invalid ? (
                    <Mail className="h-4 w-4 text-bad" aria-label="Email inválido" />
                  ) : (
                    <Mail className="h-4 w-4 text-muted-2" aria-label="Email" />
                  )}
                </div>
              </td>
              <td className="px-3 py-2 text-right">
                <button
                  type="button"
                  onClick={() => handleRemind(row.oldest_charge_id, row.client_name)}
                  disabled={resend.isPending}
                  className="inline-flex items-center gap-1 rounded-md border border-line bg-panel-2 px-2 py-1 text-xs font-medium text-ink-2 transition-opacity hover:opacity-80 disabled:opacity-50"
                >
                  <Bell className="h-3 w-3" />
                  Lembrar
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-lg border border-line bg-panel">
      <div className="border-b border-line px-4 py-3">
        <h3 className="text-sm font-medium text-ink">{title}</h3>
      </div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

function Skeleton() {
  return <div className="h-64 animate-pulse rounded-lg border border-line bg-panel-2" />;
}
